import React, { useState } from 'react'
import { arrayOf, string, shape } from 'prop-types'
import { branch } from 'baobab-react/higher-order'

import SectionContainer from 'components/atoms/SectionContainer'
import SectionTitle from 'components/atoms/SectionTitle'
import Container from 'components/atoms/Container'
import Row from 'components/atoms/Row'
import Col from 'components/atoms/Col'
import Typography from 'components/atoms/Typography'
import Button from 'components/atoms/Button'
import PinPointIcon from 'components/atoms/SVGIcons/PinPoint'

import Relazsizes from 'relazsizes'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'

import config from 'config'

const {
  google_maps_api_key: gmapsAPIKey,
  assets_url: assetsUrl,
} = config

const SchoolCampusItem = ({ campus, schoolSlug }) => (
  <div className="school-locations__list-item">
    <h4 className="school-locations__campus-name">
      { campus.name }
    </h4>
    {
      campus.address &&
      <Typography component="p" variant="body">
        { campus.address }
      </Typography>
    }
    <div className="school-locations__contacts">
      {
        campus.email && (
          <a href={`mailto:${campus.email}`} className="school-locations__contact-info">
            { campus.email }
          </a>
        )
      }
      {
        campus.phone_number && (
          <a href={`tel:${campus.phone_number}`} className="school-locations__contact-info">
            { campus.phone_number }
          </a>
        )
      }
    </div>
    {
      campus.faculties.length > 0 &&
      <div className="school-locations__faculties-list">
        <h4 className="school-locations__faculties-heading">
          Fakultas:
        </h4>
        {
          campus.faculties.map((faculty, idx) => (
            <a href={`/directory/${schoolSlug}/faculties/${faculty.slug}`} className="school-locations__faculty-link" key={`fclty-${faculty.id}-${idx}`}>
              { faculty.name }
            </a>
          ))
        }
      </div>
    }
    <style jsx>
      {`
        .school-locations {
          &__list-item {
            padding: 32px 0;
            border-bottom: solid 1px #bed2e8;
          }
          &__contacts {
            margin: 0px -8px 16px;
          }
          &__contact-info {
            padding: 8px 16px;
            background-color: #bcefe2;
            color: #485c77;
            font-weight: bold;
            border-radius: 16px;
            margin: 8px;
            display: inline-block;
          }
          &__campus-name,
          &__faculties-heading {
            font-size: 16px;
            font-weight: bold;
            line-height: 2;
            color: #485c77;
          }
          &__faculties-list {
            margin: 16px 0 0;
          }
          &__faculty-link {
            display: block;
            font-size: 16px;
            color: #0fc3c4;
            line-height: 2;
            margin-bottom: .5em;
          }
        }
      `}
    </style>
  </div>
)

const SchoolCampusItemWithSchoolSlug = branch({
  schoolSlug: ['serverProps', 'school', 'slug'],
}, SchoolCampusItem)

const SchoolLocations = ({ campuses }) => {
  const [showMaps, setShowMaps] = useState(false)
  const mainCampusCoordinate = campuses[0] && campuses[0].coordinate && campuses[0].coordinate.split(',')
  const mainCampusLatLang = mainCampusCoordinate && ({
    lat: parseFloat(mainCampusCoordinate[0]),
    lng: parseFloat(mainCampusCoordinate[1]),
  })

  return (
    <div className="school-locations" id="locations">
      <SectionContainer paddingTop={120} paddingBottom={80}>
        <SectionTitle title="Lokasi Kampus" />
        <div className="school-locations__content">
          <Row>
            <Col md="8">
              {showMaps ?
                <LoadScript
                  id="script-loader"
                  googleMapsApiKey={gmapsAPIKey}
                >
                  <GoogleMap
                    id="school-locations-map"
                    mapContainerStyle={{
                      height: '600px',
                      width: '100%',
                    }}
                    zoom={15}
                    center={mainCampusLatLang}
                  >
                    <Marker
                      position={mainCampusLatLang}
                    />
                  </GoogleMap>
                </LoadScript> :
                <div className="school-locations__map-placeholder">
                  <Relazsizes
                    className="school-locations__img-placeholder"
                    dataSrc={`${assetsUrl}/assets/img/maps-placeholder.jpg`}
                    alt="Lokasi Kampus | Quipper Campus"
                  />
                  {
                    campuses[0] && campuses[0].coordinate &&
                    <div className="school-locations__overlay">
                      <Button onClick={() => setShowMaps(true)} className="school-locations__show-btn">
                        <PinPointIcon fill="#fff"/>
                        Lihat Lokasi
                      </Button>
                    </div>
                  }
                </div>
              }
            </Col>
            <Col md={{ size: 4, order: 'first' }}>
              <div className="school-locations__list">
                <Container>
                  {
                    campuses.map(campus =>
                      <SchoolCampusItemWithSchoolSlug campus={campus} key={`sch-itm-${campus.id}`}/>
                    )
                  }
                </Container>
              </div>
            </Col>
          </Row>
        </div>
      </SectionContainer>
      <style jsx>
        {`
          .school-locations {
            &__list {
              background-color: #e3ecf5;
              height: 100%;
              width: 100%;
              overflow-y: auto;
              &::-webkit-scrollbar-track {
                margin-top: 8px;
                border-radius: 10px;
                width: 5px;
                background-color: #fff;
              }
              &::-webkit-scrollbar {
                border-radius: 10px;
                width: 5px;
                background-color: #fff;
              }
              &::-webkit-scrollbar-thumb {
                border-radius: 10px;
                width: 5px;
                background-color: #bed2e8;
              }
              max-height: 600px;
              @media screen and (min-width: 768px) {
                height: 600px;
              }
            }
            &__content {
              background-color: #e3ecf5;
              :global(.cmps-row) {
                @media screen and (min-width: 768px) {
                  grid-gap: 0;
                }
              }
            }
            &__map-placeholder {
              position: relative;
            }
            &__overlay {
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: 100%;

              display: flex;
              justify-content: center;
              align-items: center;
              &::before {    
                content: "";
                position: absolute;
                width: 100%;
                height: 100%;
                opacity: 0.8;
                background-color: #1a344f;
              }
            }
            :global(&__img-placeholder) {
              width: 100%;
              height: 600px;
              object-fit: cover;
              @media screen and (max-width: 767px) {
                height: 375px !important;
              }
            }
            :global(&__show-btn) {
              font-weight: bold;
              display: flex;
              align-items: center;
              :global(svg) {
                margin-right: 8px;
              }
            }
          }
          :global(#school-locations-map) {
            @media screen and (max-width: 767px) {
              height: 375px !important;
            }
          }
        `}
      </style>
    </div>
  )
}

const FacultyInCampus = shape({
  id: string,
  name: string,
  slug: string,
})

const CampusPropType = shape({
  address: string,
  coordinate: string,
  email: string,
  faculties: arrayOf(FacultyInCampus),
  id: string,
  name: string,
  phone_number: string,
  province: string,
})

SchoolCampusItem.propTypes = {
  campus: CampusPropType,
  schoolSlug: string,
}

SchoolLocations.propTypes = {
  campuses: arrayOf(CampusPropType),
}

export default SchoolLocations
